/**
 * <infinite-scroll> — appends the next page of a paginated product grid when
 * the element scrolls into view. Rendered by sections/main-collection-product-grid.liquid
 * inside #ProductGridContainer, so Dawn's facets.js recreates it on every
 * filter / sort re-render with the correct first "next" URL.
 *
 * data-next-url    Liquid paginate.next.url for the page to load next
 * data-section-id  section id, used for the Section Rendering API fetch
 */
if (!customElements.get('infinite-scroll')) {
  customElements.define(
    'infinite-scroll',
    class InfiniteScroll extends HTMLElement {
      connectedCallback() {
        this.container = this.closest('#ProductGridContainer');
        this.grid = this.container ? this.container.querySelector('#product-grid') : null;
        this.pagination = this.container ? this.container.querySelector('.pagination-wrapper') : null;
        if (!this.grid || !this.dataset.nextUrl || !('IntersectionObserver' in window)) return;

        if (this.pagination) this.pagination.hidden = true;
        this.loading = false;

        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
          },
          { rootMargin: '600px 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      loadNext() {
        if (this.loading || !this.dataset.nextUrl) return;
        this.loading = true;
        this.setAttribute('aria-busy', 'true');

        const url = new URL(this.dataset.nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);

        fetch(url.toString())
          .then((response) => {
            if (!response.ok) throw new Error(`Infinite scroll: ${response.status} for ${url}`);
            return response.text();
          })
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const newGrid = doc.querySelector('#product-grid');
            const newItems = newGrid ? Array.from(newGrid.children) : [];
            newItems.forEach((item) => this.grid.appendChild(item));

            this.revealOnScroll(newItems);

            const next = doc.querySelector('infinite-scroll');
            if (next && next.dataset.nextUrl && newItems.length) {
              this.dataset.nextUrl = next.dataset.nextUrl;
              this.loading = false;
              this.removeAttribute('aria-busy');
              // The sentinel may still be in view after appending a short page.
              window.requestAnimationFrame(() => {
                if (this.isConnected && this.getBoundingClientRect().top < window.innerHeight + 600) this.loadNext();
              });
            } else {
              this.finish();
            }
          })
          .catch((error) => {
            console.error(error);
            // Fall back to the classic page links so nothing is unreachable.
            if (this.pagination) this.pagination.hidden = false;
            this.finish();
          });
      }

      /**
       * Mirrors Dawn's animations.js scroll-trigger reveal, but only for the
       * appended cards. Passing the whole grid to initializeScrollAnimationTrigger
       * would re-observe already-revealed cards and animate them a second time
       * when the customer scrolls back up.
       */
      revealOnScroll(items) {
        const triggers = items.filter((item) => item.classList && item.classList.contains('scroll-trigger'));
        if (!triggers.length) return;
        const observer = new IntersectionObserver(
          (entries, obs) => {
            entries.forEach((entry, index) => {
              const el = entry.target;
              if (entry.isIntersecting) {
                if (el.classList.contains('scroll-trigger--offscreen')) {
                  el.classList.remove('scroll-trigger--offscreen');
                  if (el.hasAttribute('data-cascade')) el.style.setProperty('--animation-order', index);
                }
                obs.unobserve(el);
              } else {
                el.classList.add('scroll-trigger--offscreen');
                el.classList.remove('scroll-trigger--cancel');
              }
            });
          },
          { rootMargin: '0px 0px -50px 0px' }
        );
        triggers.forEach((el) => observer.observe(el));
      }

      finish() {
        this.loading = false;
        delete this.dataset.nextUrl;
        if (this.observer) this.observer.disconnect();
        this.remove();
      }
    }
  );
}
